if (!customElements.get('maston-scene-carousel')) {
  /**
   * Peek carousel. Active cards stay centered; first and last cards pin to
   * the rail edges, and previous always changes travel.
   */
  class MastonSceneCarousel extends HTMLElement {
    #abortController;
    #currentIndex = 0;
    #isAnimating = false;
    #pointerStartX = 0;
    #resizeFrame = 0;
    #scrollFrame = 0;
    #scrollAnimFrame = 0;
    #snapRestoreFrame = 0;
    #captionTimer = 0;
    #pendingCaptionIndex = -1;
    #renderedCaptionIndex = -1;
    #reducedMotionQuery = null;
    #progressFill = null;
    #progressAnimation = null;
    #viewportObserver = null;
    #autoplayRestartTimer = 0;
    #suppressScrollRestartUntil = 0;
    #isInViewport = false;
    #isHoverPaused = false;

    connectedCallback() {
      if (this.#abortController) return;

      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.viewport = this.querySelector('[data-scene-viewport]');
      this.previousButton = this.querySelector('[data-scene-previous]');
      this.nextButton = this.querySelector('[data-scene-next]');
      this.controls = this.querySelector('[data-scene-controls]');
      this.activeCopy = this.querySelector('[data-scene-copy]');
      this.#progressFill = this.querySelector('[data-scene-autoplay-fill]');
      this.slides = Array.from(this.querySelectorAll('.shopify-block__maston-scene-card'));
      this.#reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion = this.#reducedMotionQuery.matches;

      if (!this.viewport || !this.slides.length) return;

      this.#bindEvents(signal);
      this.classList.add('is-ready');
      this.#syncCaptionHeight();
      this.#sync();
      this.#syncAutoplayClock();
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#abortController = null;
      this.#teardown();
    }

    /**
     * Complete cards in view. Peek comes from the leftover viewport beside the content column.
     * @returns {number}
     */
    #visibleCount() {
      return 1;
    }

    /**
     * @returns {number}
     */
    #maxIndex() {
      if (!this.slides.length) return 0;
      return Math.max(0, this.slides.length - this.#visibleCount());
    }

    /**
     * UX breakpoint table: 380 / 450 / 500.
     * @returns {number}
     */
    #scrollDuration() {
      if (window.matchMedia('(min-width: 1280px)').matches) return 500;
      if (window.matchMedia('(min-width: 768px)').matches) return 450;
      return 380;
    }

    /**
     * @param {AbortSignal} signal
     */
    #bindEvents(signal) {
      this.previousButton?.addEventListener('click', () => this.#move(-1), { signal });
      this.nextButton?.addEventListener('click', () => this.#move(1), { signal });
      this.viewport?.addEventListener('scroll', () => this.#handleScroll(), { signal, passive: true });
      this.viewport?.addEventListener('pointerdown', this.#handlePointerDown, { signal, passive: true });
      this.viewport?.addEventListener('click', this.#handleSlideClick, { signal });
      document.addEventListener('keydown', this.#handleKeydown, { signal });
      window.addEventListener('resize', this.#handleResize, { signal, passive: true });
      this.#reducedMotionQuery?.addEventListener('change', this.#handleReducedMotionChange, { signal });
      document.addEventListener('visibilitychange', this.#handleAutoplayChange, { signal });
      this.nextButton?.addEventListener('pointerenter', this.#handleAutoplayPointerEnter, { signal });
      this.nextButton?.addEventListener('pointerleave', this.#handleAutoplayPointerLeave, { signal });

      this.#viewportObserver = new IntersectionObserver(
        (entries) => {
          this.#isInViewport = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0);
          this.#syncAutoplayClock();
        },
        { threshold: 0.35 },
      );
      this.#viewportObserver.observe(this);

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });
      }
    }

    /**
     * One-card step. Previous skips indexes that still sit on the end rail.
     * @param {number} direction
     */
    #move(direction) {
      if (this.#isAnimating) return;

      const maxIndex = this.#maxIndex();
      if (maxIndex <= 0) return;

      const looping = this.#canAutoplay();
      let nextIndex = this.#currentIndex + direction;

      if (looping) {
        if (nextIndex < 0) nextIndex = maxIndex;
        else if (nextIndex > maxIndex) nextIndex = 0;
      } else {
        if (direction < 0 && this.previousButton?.disabled) return;
        if (direction > 0 && this.nextButton?.disabled) return;

        nextIndex = Math.max(0, Math.min(maxIndex, nextIndex));

        if (direction < 0 && nextIndex > 0) {
          const currentTravel = this.#targetLeft(this.#currentIndex);
          while (nextIndex > 0 && Math.abs(this.#targetLeft(nextIndex) - currentTravel) <= 1) {
            nextIndex -= 1;
          }
        }
      }

      if (nextIndex === this.#currentIndex) return;

      this.#scrollToIndex(nextIndex);
    }

    /**
     * IFA targeting: 0 on the first card, maxScroll on the last page,
     * centered travel for every card in between.
     * @param {number} index
     * @returns {number}
     */
    #targetLeft(index) {
      const scroller = this.viewport;
      const slide = this.slides[index];
      if (!slide || !scroller) return 0;

      const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      if (index === 0) return 0;
      if (index >= this.#maxIndex()) return maxScroll;
      return this.#centerOffset(slide, scroller);
    }

    /**
     * @param {Element} slide
     * @param {HTMLElement} scroller
     * @returns {number}
     */
    #centerOffset(slide, scroller) {
      const scrollerRect = scroller.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const targetLeft =
        scroller.scrollLeft +
        slideRect.left -
        scrollerRect.left -
        (scroller.clientWidth - slideRect.width) / 2;
      const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);

      return Math.min(maxScroll, Math.max(0, targetLeft));
    }

    /**
     * @param {number} index
     * @param {'smooth' | 'auto'} [behavior]
     */
    #scrollToIndex(index, behavior = 'smooth') {
      const scroller = this.viewport;
      const slide = this.slides[index];
      if (!slide || !scroller) return;

      this.#stopAutoplayClock();
      this.#currentIndex = index;
      this.#sync();

      const targetLeft = this.#targetLeft(index);
      const distance = Math.abs(targetLeft - scroller.scrollLeft);
      const duration = Math.min(720, Math.max(this.#scrollDuration(), distance * 0.55));

      if (behavior === 'auto' || this.reducedMotion) {
        this.#stopTween();
        scroller.scrollLeft = targetLeft;
        this.#endTween();
        return;
      }

      this.#animateScrollTo(scroller, targetLeft, duration);
    }

    /**
     * @param {HTMLElement} scroller
     * @param {number} targetLeft
     * @param {number} duration
     */
    #animateScrollTo(scroller, targetLeft, duration) {
      this.#stopTween();

      const startLeft = scroller.scrollLeft;
      const delta = targetLeft - startLeft;

      if (Math.abs(delta) < 1) {
        scroller.scrollLeft = targetLeft;
        this.#endTween();
        return;
      }

      this.#isAnimating = true;
      scroller.classList.add('is-scrolling');

      const startTime = performance.now();

      const tick = (now) => {
        const progress = Math.min(1, (now - startTime) / duration);
        scroller.scrollLeft = startLeft + delta * this.#ease(progress);

        if (progress < 1) {
          this.#scrollAnimFrame = window.requestAnimationFrame(tick);
          return;
        }

        scroller.scrollLeft = targetLeft;
        this.#releaseScrollLock();
      };

      this.#scrollAnimFrame = window.requestAnimationFrame(tick);
    }

    /**
     * CSS cubic-bezier(0.25, 1, 0.5, 1).
     * @param {number} t
     * @returns {number}
     */
    #ease(t) {
      if (t <= 0) return 0;
      if (t >= 1) return 1;

      const p1x = 0.25;
      const p1y = 1;
      const p2x = 0.5;
      const p2y = 1;
      let u = t;

      for (let i = 0; i < 6; i += 1) {
        const x = ((1 - 3 * p2x + 3 * p1x) * u + (3 * p2x - 6 * p1x)) * u * u + 3 * p1x * u;
        const dx = 3 * (1 - 3 * p2x + 3 * p1x) * u * u + 2 * (3 * p2x - 6 * p1x) * u + 3 * p1x;
        if (Math.abs(dx) < 1e-6) break;
        u -= (x - t) / dx;
      }

      return ((1 - 3 * p2y + 3 * p1y) * u + (3 * p2y - 6 * p1y)) * u * u + 3 * p1y * u;
    }

    #stopTween() {
      window.cancelAnimationFrame(this.#scrollAnimFrame);
      window.cancelAnimationFrame(this.#snapRestoreFrame);
      this.#scrollAnimFrame = 0;
      this.#snapRestoreFrame = 0;
    }

    #endTween() {
      this.#isAnimating = false;
      this.viewport?.classList.remove('is-scrolling');
      this.#suppressScrollRestartUntil = performance.now() + 200;
      this.#syncAutoplayClock();
    }

    /**
     * Restore snap after the final scrollLeft has painted.
     */
    #releaseScrollLock() {
      this.#stopTween();

      this.#snapRestoreFrame = window.requestAnimationFrame(() => {
        this.#snapRestoreFrame = window.requestAnimationFrame(() => {
          this.#endTween();
          this.#snapRestoreFrame = 0;
        });
      });
    }

    #handleScroll() {
      if (this.#isAnimating) return;

      window.cancelAnimationFrame(this.#scrollFrame);
      this.#scrollFrame = window.requestAnimationFrame(() => {
        const scroller = this.viewport;
        if (!scroller || !this.slides.length) return;

        const left = scroller.scrollLeft;
        const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);

        if (left <= 1) {
          this.#currentIndex = 0;
          this.#sync();
          this.#scheduleAutoplayRestart();
          return;
        }

        if (left >= maxScroll - 1) {
          this.#currentIndex = this.#maxIndex();
          this.#sync();
          this.#scheduleAutoplayRestart();
          return;
        }

        let closestIndex = 0;
        let closestDistance = Infinity;
        const scrollerRect = scroller.getBoundingClientRect();
        const centerX = scrollerRect.left + scroller.clientWidth / 2;

        this.slides.forEach((slide, index) => {
          const slideRect = slide.getBoundingClientRect();
          const distance = Math.abs(slideRect.left + slideRect.width / 2 - centerX);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        this.#currentIndex = Math.min(closestIndex, this.#maxIndex());
        this.#sync();
        this.#scheduleAutoplayRestart();
      });
    }

    #handlePointerDown = (event) => {
      this.#pointerStartX = event.clientX;
    };

    #handleSlideClick = (event) => {
      if (this.#isAnimating) return;
      if (!(event.target instanceof Element)) return;
      if (Math.abs(event.clientX - this.#pointerStartX) > 8) return;

      const slide = event.target.closest('.shopify-block__maston-scene-card');
      if (!slide || !this.contains(slide)) return;

      const index = this.slides.indexOf(slide);
      if (index < 0 || index === this.#currentIndex) return;

      this.#scrollToIndex(Math.min(index, this.#maxIndex()));
    };

    #handleKeydown = (event) => {
      if (!this.contains(document.activeElement)) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;
      if (event.target instanceof HTMLSelectElement) return;
      if (event.target instanceof HTMLElement && event.target.isContentEditable) return;

      event.preventDefault();
      this.#move(event.key === 'ArrowRight' ? 1 : -1);
    };

    #handleResize = () => {
      window.cancelAnimationFrame(this.#resizeFrame);
      this.#resizeFrame = window.requestAnimationFrame(() => {
        this.#syncCaptionHeight();
        this.#scrollToIndex(Math.min(this.#currentIndex, this.#maxIndex()), 'auto');
      });
    };

    #handleBlockSelect = (event) => {
      if (!(event.target instanceof Element) || !this.contains(event.target)) return;

      const slide = event.target.closest('.shopify-block__maston-scene-card');
      if (!slide || !this.contains(slide)) return;

      const index = this.slides.indexOf(slide);
      if (index >= 0) this.#scrollToIndex(Math.min(index, this.#maxIndex()));
    };

    #sync() {
      const maxIndex = this.#maxIndex();
      const looping = this.#canAutoplay();
      this.previousButton?.toggleAttribute('disabled', this.#currentIndex <= 0 && !looping);
      this.nextButton?.toggleAttribute('disabled', this.#currentIndex >= maxIndex && !looping);
      this.controls?.toggleAttribute('hidden', this.slides.length <= 1);

      this.slides.forEach((slide, index) => {
        const isActive = index === this.#currentIndex;
        slide.classList.toggle('is-active', isActive);
        slide.querySelector('[data-scene-card]')?.toggleAttribute('aria-current', isActive);
        slide
          .querySelector('[data-scene-features]')
          ?.toggleAttribute('aria-hidden', !isActive);
      });

      this.#syncCaption();
    }

    #syncCaption() {
      if (!this.activeCopy) return;
      if (this.#renderedCaptionIndex === this.#currentIndex && this.#pendingCaptionIndex < 0) return;
      if (this.#pendingCaptionIndex === this.#currentIndex) return;

      window.clearTimeout(this.#captionTimer);
      this.#pendingCaptionIndex = this.#currentIndex;

      const currentCaption = this.activeCopy.firstElementChild;
      if (currentCaption && !this.reducedMotion) {
        currentCaption.classList.remove('is-entering');
        currentCaption.classList.add('is-leaving');
        this.#captionTimer = window.setTimeout(() => {
          this.#renderCaption(this.#pendingCaptionIndex);
        }, 150);
        return;
      }

      this.#renderCaption(this.#pendingCaptionIndex);
    }

    #renderCaption(index) {
      const caption = this.#createCaption(index);
      if (!caption) {
        this.activeCopy.replaceChildren();
        this.#renderedCaptionIndex = index;
        this.#pendingCaptionIndex = -1;
        return;
      }

      if (!this.reducedMotion) caption.classList.add('is-entering');

      this.activeCopy.replaceChildren(caption);
      this.#renderedCaptionIndex = index;
      this.#pendingCaptionIndex = -1;
    }

    /**
     * Clone the source caption and attach mobile feature rows, including icons.
     * @param {number} index
     * @returns {HTMLElement | null}
     */
    #createCaption(index) {
      const slide = this.slides[index];
      const source = slide?.querySelector('[data-scene-caption-source]');
      if (!source) return null;

      const caption = source.cloneNode(true);
      caption.classList.remove('maston-scene-carousel__caption--source');
      caption.removeAttribute('data-scene-caption-source');
      caption.toggleAttribute('data-active', true);

      const features = Array.from(slide.querySelectorAll('[data-scene-feature]')).filter((feature) => {
        const text = feature.querySelector('.maston-scene-carousel__feature-text')?.textContent.trim();
        const icon = feature.querySelector('.maston-scene-carousel__feature-icon');
        return Boolean(text || icon);
      });

      if (features.length) {
        const featureList = document.createElement('ul');
        featureList.className = 'maston-scene-carousel__caption-features';
        featureList.setAttribute('role', 'list');

        features.forEach((feature) => {
          const item = document.createElement('li');
          item.className = 'maston-scene-carousel__caption-feature';

          const icon = feature.querySelector('.maston-scene-carousel__feature-icon');
          if (icon) item.append(icon.cloneNode(true));

          const text = feature.querySelector('.maston-scene-carousel__feature-text')?.textContent.trim();
          if (text) {
            const label = document.createElement('span');
            label.className = 'maston-scene-carousel__caption-feature-text';
            label.textContent = text;
            item.append(label);
          }

          featureList.append(item);
        });

        caption.append(featureList);
      }

      return caption;
    }

    #syncCaptionHeight() {
      if (!this.activeCopy) return;

      const copyWidth = this.activeCopy.clientWidth;
      if (!copyWidth) return;

      const measure = document.createElement('div');
      measure.className = 'maston-scene-carousel__copy maston-scene-carousel__copy-measure';
      measure.style.inlineSize = `${copyWidth}px`;
      measure.setAttribute('aria-hidden', 'true');

      this.slides.forEach((_, index) => {
        const caption = this.#createCaption(index);
        if (caption) measure.append(caption);
      });

      this.append(measure);
      const captionHeight = Math.ceil(
        Math.max(0, ...Array.from(measure.children).map((caption) => caption.getBoundingClientRect().height)),
      );
      measure.remove();
      this.style.setProperty('--maston-scene-caption-height', `${captionHeight}px`);
    }

    /**
     * Autoplay runs on every breakpoint. The next-button ring is elapsed time.
     * @returns {boolean}
     */
    #canAutoplay() {
      if (this.dataset.autoplay !== 'true') return false;
      if (this.slides.length < 2) return false;
      if (this.reducedMotion) return false;
      return true;
    }

    /**
     * @returns {boolean}
     */
    #isAutoplayPaused() {
      if (!this.#isInViewport) return true;
      if (document.hidden) return true;
      if (this.#isHoverPaused) return true;
      if (this.#isAnimating) return true;
      return false;
    }

    /**
     * @returns {number}
     */
    #autoplayDuration() {
      const parsed = Number.parseInt(this.dataset.autoplayDuration || '3000', 10);
      return Number.isFinite(parsed) ? Math.max(2000, parsed) : 3000;
    }

    #handleAutoplayChange = () => {
      this.#sync();
      this.#syncAutoplayClock();
    };

    #handleReducedMotionChange = (event) => {
      this.reducedMotion = event.matches;
      this.#handleAutoplayChange();
    };

    #handleAutoplayPointerEnter = (event) => {
      if (event.pointerType === 'touch') return;
      if (event.currentTarget !== this.nextButton) return;
      this.#isHoverPaused = true;
      this.#syncAutoplayClock();
    };

    #handleAutoplayPointerLeave = () => {
      this.#isHoverPaused = false;
      this.#syncAutoplayClock();
    };

    /**
     * Stop the clock on user scrolling, then restart after the rail settles.
     */
    #scheduleAutoplayRestart() {
      if (!this.#canAutoplay()) return;
      if (performance.now() < this.#suppressScrollRestartUntil) return;
      this.#stopAutoplayClock();
      this.#autoplayRestartTimer = window.setTimeout(() => {
        this.#syncAutoplayClock();
      }, 160);
    }

    /**
     * Advance one scene and wrap from the last card back to the first.
     */
    #advance() {
      if (this.#isAnimating) return;
      const maxIndex = this.#maxIndex();
      if (maxIndex <= 0) return;
      const nextIndex = this.#currentIndex >= maxIndex ? 0 : this.#currentIndex + 1;
      this.#scrollToIndex(nextIndex);
    }

    /**
     * Progress ring: empty at 0, fills clockwise to 1, then advances.
     */
    #startAutoplayClock() {
      this.#stopAutoplayClock();
      if (!this.#canAutoplay() || !this.#progressFill) return;

      this.#progressAnimation = this.#progressFill.animate(
        [{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }],
        { duration: this.#autoplayDuration(), easing: 'linear', fill: 'forwards' },
      );
      this.#progressAnimation.onfinish = () => {
        this.#progressAnimation?.cancel();
        this.#progressAnimation = null;
        this.#advance();
      };

      if (this.#isAutoplayPaused()) this.#progressAnimation.pause();
    }

    #stopAutoplayClock() {
      window.clearTimeout(this.#autoplayRestartTimer);
      this.#autoplayRestartTimer = 0;
      if (this.#progressAnimation) {
        this.#progressAnimation.onfinish = null;
        this.#progressAnimation.cancel();
        this.#progressAnimation = null;
      }
      this.#progressFill?.getAnimations().forEach((animation) => {
        animation.onfinish = null;
        animation.cancel();
      });
    }

    /**
     * Keep one clock in sync with viewport, hover, visibility, and motion state.
     */
    #syncAutoplayClock() {
      const canAutoplay = this.#canAutoplay();
      this.classList.toggle('is-autoplaying', canAutoplay);
      this.style.setProperty('--maston-scene-autoplay-duration', `${this.#autoplayDuration()}ms`);

      if (!canAutoplay) {
        this.#stopAutoplayClock();
        return;
      }

      if (this.#isAnimating) {
        if (this.#progressAnimation?.playState === 'running') this.#progressAnimation.pause();
        return;
      }

      if (this.#isAutoplayPaused()) {
        if (!this.#progressAnimation) this.#startAutoplayClock();
        else if (this.#progressAnimation.playState === 'running') this.#progressAnimation.pause();
        return;
      }

      if (this.#progressAnimation?.playState === 'paused') {
        this.#progressAnimation.play();
        return;
      }

      if (!this.#progressAnimation) this.#startAutoplayClock();
    }

    #teardown() {
      window.cancelAnimationFrame(this.#resizeFrame);
      window.cancelAnimationFrame(this.#scrollFrame);
      window.clearTimeout(this.#captionTimer);
      this.#viewportObserver?.disconnect();
      this.#viewportObserver = null;
      this.#stopTween();
      this.#isAnimating = false;
      this.viewport?.classList.remove('is-scrolling');
      this.#stopAutoplayClock();
    }
  }

  customElements.define('maston-scene-carousel', MastonSceneCarousel);
}
